import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Tooltip from '@/components/UI/Tooltip';
import { _isBlank } from '@/littledash';
import cn from 'classnames';
import type { FC } from 'react';
import type { DataTableCellIndexCoordinate } from '@/components/UI/DataTableV2/DataTable.model';
import styles from '../DataTableV2.module.scss';
import { PickerDisplayFormat, usePickerCellBehavior } from '../hooks/usePickerCellBehavior';
import { useAutoInsertOnActivate } from '../hooks/useAutoInsertOnActivate';

interface SampleCollectedAtCellProps {
  coordinate: DataTableCellIndexCoordinate;
  positionOffset: { x: number; y: number };
}

/**
 * Sentinel value sent to API to trigger server-side timestamp calculation.
 * The API intercepts this value and replaces it with the actual current timestamp.
 */
const TIMESTAMP_PLACEHOLDER = '-1';

export const SampleCollectedAtCell: FC<SampleCollectedAtCellProps> = ({ coordinate, positionOffset }) => {
  const { cell, picker, mouseHandlers } = usePickerCellBehavior({
    coordinate,
    positionOffset,
    displayFormat: PickerDisplayFormat.DateTimeHoursMinutesSeconds,
  });

  // Auto-insert placeholder value when cell activated on empty cell
  // API will calculate actual timestamp server-side
  useAutoInsertOnActivate({
    coordinate,
    getValue: () => TIMESTAMP_PLACEHOLDER,
  });

  const showLoading = cell.state === 'Loading' || cell.state === 'Saving';
  const isEmptyOrPlaceholder = _isBlank(cell.value) || cell.value === TIMESTAMP_PLACEHOLDER;

  return (
    <div
      className={cn(styles.CellContainer, { [styles.groupBorder]: cell.hasGroupBorder })}
      style={picker.positionStyle}
      onMouseDown={mouseHandlers.onMouseDown}
      onMouseEnter={mouseHandlers.onMouseEnter}
      onMouseUp={mouseHandlers.onMouseUp}
      onDoubleClick={picker.activate}
    >
      {showLoading ? (
        <div className={cn(styles.Cell, styles.Loading)} />
      ) : (
        <Tooltip render={cell.tooltip}>
          <div
            className={cn(
              styles.Cell,
              styles[cell.selectionState],
              { [styles.Active]: picker.active },
              styles[cell.state]
            )}
            data-test-component="SampleCollectedAtCell"
            data-test-element="collect-cell"
            data-test-key={`(${coordinate.column},${coordinate.row})`}
          >
            {isEmptyOrPlaceholder ? (
              <span className="link blue">+ Collect</span>
            ) : (
              <DateTimeRenderer value={cell.value ?? undefined} defaultResponse={cell.value ?? undefined} />
            )}
            <input
              className={styles.TimestampInput}
              style={{ visibility: picker.active ? 'visible' : 'hidden' }}
              ref={picker.inputRef}
              type="datetime-local"
              onChange={picker.handleInputChange}
            />
          </div>
        </Tooltip>
      )}
    </div>
  );
};
